import React, {useCallback, useEffect, useRef, useState} from "react";
import {Container} from "react-bootstrap";
import Pagination from "react-bootstrap/cjs/Pagination";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faTrashAlt, faPlus, faSearch} from "@fortawesome/free-solid-svg-icons";
import ModalDommus from "../../components/Modal";
import FormContato from "./FormContato/index";
import ContatoService from "../../services/ContatoService";
import {trackPromise} from "react-promise-tracker";
import Swal from "sweetalert2";
import {successToast} from "../../components/DommusToast";
import moment from "moment";
import "moment/locale/pt-br";
import { celularMask } from "../../components/InputTextMask";
import "./index.css";

moment.locale("pt-br");

const CORES_AVATAR = [
    "#4CAF50", "#2196F3", "#9C27B0", "#FF9800", "#E91E63",
    "#00BCD4", "#795548", "#607D8B", "#F44336", "#3F51B5",
];

function obterCorAvatar(nome) {
    if (!nome) return "#607D8B";
    let hash = 0;
    for (let i = 0; i < nome.length; i++) hash = nome.charCodeAt(i) + ((hash << 5) - hash);
    return CORES_AVATAR[Math.abs(hash) % CORES_AVATAR.length];
}

function obterIniciaisAvatar(nome) {
    if (!nome) return "?";
    const partes = nome.trim().split(" ").filter(Boolean);
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

export default function AgendaContatos() {
    const [openModal, setOpenModal] = useState(false);
    const [filtroBusca, setFiltroBusca] = useState("");

    const [contatoSelecionado, setContatoSelecionado] = useState(null);
    const [contatos, setContatos] = useState([]);
    const [selecionados, setSelecionados] = useState([]);
    const [metricas, setMetricas] = useState({
        total: 0,
        comNumero: 0,
        comEmail: 0,
        adicionadosHoje: 0,
    });
    const masterCheckboxRef = useRef(null);
    const [pagina, setPagina] = useState(1);
    const [tamanhoPagina, setTamanhoPagina] = useState(8);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [totalPaginas, setTotalPaginas] = useState(1);

    const carregarContatos = useCallback(() => {
        const params = {
            page: pagina,
            tamanho_pagina: tamanhoPagina,
            per_page: tamanhoPagina,
        };
        if (filtroBusca && filtroBusca.trim()) params.nome = filtroBusca.trim();

        trackPromise(ContatoService.index(params)).then((response) => {
            const res = response.data || {};
            const items = res.data || res.items || res || [];

            const meta = res.meta || res.pagination || {};
            const total = meta.total || res.total || 0;
            const perPage = meta.per_page || meta.perPage || tamanhoPagina;
            const lastPage = meta.last_page || meta.lastPage || (perPage ? Math.ceil(total / perPage) : 1);

            setContatos(items || []);
            setTotalRegistros(Number(total) || 0);
            setTotalPaginas(Number(lastPage) || 1);
        }).catch(() => {
            Swal.fire("Ops!", "Erro ao carregar contatos.", "error");
        });
    }, [pagina, tamanhoPagina, filtroBusca]);

    const carregarMetricas = useCallback(() => {
        trackPromise(ContatoService.metricas())
            .then((response) => {
                const dados = response.data || {};
                setMetricas({
                    total: Number(dados.total) || 0,
                    comNumero: Number(dados.comNumero) || 0,
                    comEmail: Number(dados.comEmail) || 0,
                    adicionadosHoje: Number(dados.adicionadosHoje) || 0,
                });
            });
    }, []);

    // recarrega quando pagina/tamanho/filtro mudarem
    useEffect(() => {
        carregarContatos();
        carregarMetricas();
    }, [carregarContatos, carregarMetricas]);

    // ao alterar filtro, resetar página para 1
    useEffect(() => {
        setPagina(1);
    }, [filtroBusca]);

    // atualizar estado do checkbox
    useEffect(() => {
        if (!masterCheckboxRef.current) return;
        const totalVisiveis = contatos.length;
        const totalSelecionados = selecionados.length;
        masterCheckboxRef.current.indeterminate = totalSelecionados > 0 && totalSelecionados < totalVisiveis;
    }, [selecionados, contatos]);
    

    const handleSalvar = (data) => {
        setOpenModal(false);
        const metodo = contatoSelecionado?.id_contato ? ContatoService.update(contatoSelecionado.id_contato, data) : ContatoService.store(data);

        trackPromise(metodo)
            .then(() => {
                carregarContatos();
                setContatoSelecionado(null);
                carregarMetricas();
                successToast.fire(
                    contatoSelecionado?.id_contato ? "Contato atualizado com sucesso!" : "Contato cadastrado com sucesso!"
                );
            })
            .catch(() => {
                Swal.fire("Ops!", "Erro ao salvar contato. Tente novamente.", "error");
            });
    };

    const handleRemover = useCallback((contato) => {
        Swal.fire({
            position: "center",
            icon: "question",
            html: `Deseja remover o contato: <br><b>${contato.nome}</b>?`,
            showCancelButton: true,
            confirmButtonText: "Sim, remover",
            cancelButtonText: "Não, manter",
            width: "400px",
            customClass: {
                confirmButton: "btn btn-danger mr-2",
                cancelButton: "btn btn-secondary",
            },
            buttonsStyling: false,
        }).then((result) => {
            if (result.isConfirmed) {
                trackPromise(ContatoService.destroy(contato.id_contato))
                    .then(() => {
                        carregarContatos();
                        carregarMetricas();
                        setSelecionados([]);
                        successToast.fire("Contato removido com sucesso!");
                    })
                    .catch(() => {
                        Swal.fire("Ops!", "Erro ao remover contato. Tente novamente.", "error");
                    });
            }
        });
    }, []);

    const isSelecionado = (id) => selecionados.includes(id);

    const handleToggleSelecionado = (id) => {
        setSelecionados((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            return [...prev, id];
        });
    };

    const handleToggleTodos = (e) => {
        if (e.target.checked) {
            setSelecionados(contatos.map((c) => c.id_contato));
        } else {
            setSelecionados([]);
        }
    };

    const handleRemoverSelecionados = () => {
        if (selecionados.length === 0) return;

        Swal.fire({
            position: "center",
            icon: "question",
            html: `Deseja remover ${selecionados.length} contato(s)?`,
            showCancelButton: true,
            confirmButtonText: "Sim, remover",
            cancelButtonText: "Não, manter",
            width: "400px",
            customClass: {
                confirmButton: "btn btn-danger mr-2",
                cancelButton: "btn btn-secondary",
            },
            buttonsStyling: false,
        }).then((result) => {
            if (result.isConfirmed) {
                trackPromise(ContatoService.destroyEmMassa(selecionados))
                    .then(() => {
                        carregarContatos();
                        carregarMetricas();
                        setSelecionados([]);
                        successToast.fire(`${selecionados.length} contato(s) removido(s) com sucesso!`);
                    })
                    .catch(() => {
                        Swal.fire("Ops!", "Erro ao remover contatos. Tente novamente.", "error");
                    });
            }
        });
    };
    
    const handleSetAtivo = (contato, valor) => {
        if ((contato.ativo || '0') === valor) return;

        trackPromise(ContatoService.toggleAtivo(contato.id_contato, valor))
            .then(() => {
                carregarContatos();
                carregarMetricas();
                successToast.fire(valor === '1' ? 'Contato ativado com sucesso!' : 'Contato desativado com sucesso!');
            })
            .catch(() => {
                Swal.fire("Ops!", "Erro ao alterar status do contato. Tente novamente.", "error");
            });
    };

    return (
        <>
            <Container fluid className="container-agenda-contatos">
                <div className="agenda-header">
                    <h5 className="agenda-titulo">Agenda de Contatos</h5>
                    <div className="agenda-header-acoes">
                        <div className="agenda-busca-wrapper">
                            <FontAwesomeIcon icon={faSearch} className="agenda-busca-icone"/>
                            <input
                                type="text"
                                placeholder="Buscar por nome, telefone ou e-mail"
                                className="agenda-busca-input"
                                value={filtroBusca}
                                onChange={(e) => setFiltroBusca(e.target.value)}
                            />
                        </div>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            {selecionados.length > 0 && (
                                <button
                                    className="btn btn-danger"
                                    onClick={handleRemoverSelecionados}
                                    style={{marginRight: 8}}
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} style={{marginRight: 6}}/>
                                    Remover selecionados ({selecionados.length})
                                </button>
                            )}

                            <button
                                className="btn-novo-contato"
                                onClick={() => {
                                    setContatoSelecionado(null);
                                    setOpenModal(true);
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} style={{marginRight: 6}}/>
                                Novo Contato
                            </button>
                        </div>
                    </div>
                </div>

                <div className="agenda-metricas">
                    <div className="metrica-card">
                        <span className="metrica-label">TOTAL DE CONTATOS</span>
                        <span className="metrica-valor">{metricas.total.toLocaleString("pt-BR")}</span>
                    </div>
                    <div className="metrica-card">
                        <span className="metrica-label">COM NÚMERO</span>
                        <span
                            className="metrica-valor metrica-valor--verde">{metricas.comNumero.toLocaleString("pt-BR")}</span>
                    </div>
                    <div className="metrica-card">
                        <span className="metrica-label">COM E-MAIL</span>
                        <span
                            className="metrica-valor metrica-valor--azul">{metricas.comEmail.toLocaleString("pt-BR")}</span>
                    </div>
                    <div className="metrica-card">
                        <span className="metrica-label">ADICIONADOS HOJE</span>
                        <span
                            className="metrica-valor metrica-valor--laranja">{metricas.adicionadosHoje.toLocaleString("pt-BR")}</span>
                    </div>
                </div>

                <div className="agenda-tabela-wrapper">
                    <table className="agenda-tabela">
                        <thead>
                        <tr>
                            <th style={{width: 36}}>
                                <input
                                    type="checkbox"
                                    ref={masterCheckboxRef}
                                    checked={selecionados.length > 0 && selecionados.length === contatos.length}
                                    onChange={handleToggleTodos}
                                />
                            </th>
                            <th>NOME</th>
                            <th>TELEFONE</th>
                            <th>E-MAIL</th>
                            <th>STATUS</th>
                            <th>AÇÕES</th>
                        </tr>
                        </thead>
                        <tbody>
                        {contatos.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="agenda-vazio">
                                    Nenhum contato encontrado.
                                </td>
                            </tr>
                        ) : (
                            contatos.map((contato) => {
                                const telefone = contato.telefones?.length ? celularMask(contato.telefones?.[0]?.telefone) : "—";

                                return (
                                    <tr key={contato.id_contato}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={isSelecionado(contato.id_contato)}
                                                onChange={() => handleToggleSelecionado(contato.id_contato)}
                                            />
                                        </td>
                                        <td>
                                            <div className="contato-nome-cell">
                          <span
                              className="contato-avatar"
                              style={{backgroundColor: obterCorAvatar(contato.nome)}}
                          >
                            {obterIniciaisAvatar(contato.nome)}
                          </span>
                                                <div>
                                                    <span className="contato-nome">{contato.nome}</span>
                                                    {contato.empresa && (
                                                        <span className="contato-empresa">{contato.empresa}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{telefone}</td>
                                        <td>
                                            {contato.email ? (
                                                <a href={`mailto:${contato.email}`} className="contato-email-link">
                                                    {contato.email}
                                                </a>
                                            ) : "—"}
                                        </td>
                                        <td>
                                            <div className="status-toggle" role="group" aria-label="Status do contato">
                                                <button
                                                    type="button"
                                                    className={`status-seg seg-ativo ${contato.ativo === '1' ? 'selected' : ''}`}
                                                    onClick={() => handleSetAtivo(contato, '1')}
                                                    title="Ativar"
                                                >
                                                    Ativo
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`status-seg seg-inativo ${contato.ativo !== '1' ? 'selected' : ''}`}
                                                    onClick={() => handleSetAtivo(contato, '0')}
                                                    title="Desativar"
                                                >
                                                    Inativo
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="agenda-acoes">
                                                <button
                                                    className="acao-btn acao-btn--editar"
                                                    onClick={() => {
                                                        setContatoSelecionado(contato);
                                                        setOpenModal(true);
                                                    }}
                                                    title="Editar contato"
                                                >
                                                    <FontAwesomeIcon icon={faEdit}/>
                                                </button>
                                                <button
                                                    className="acao-btn acao-btn--remover"
                                                    onClick={() => handleRemover(contato)}
                                                    title="Remover contato"
                                                >
                                                    <FontAwesomeIcon icon={faTrashAlt}/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>
                </div>

                {contatos.length > 0 && (
                    <div className="agenda-rodape">
                        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                            <span className="agenda-contagem">
                                {((pagina - 1) * tamanhoPagina + 1)}–{Math.min(pagina * tamanhoPagina, totalRegistros)} de {totalRegistros}
                            </span>
                            <Pagination>
                                <Pagination.First onClick={() => setPagina(1)} />
                                <Pagination.Prev onClick={() => { if (pagina !== 1) setPagina(pagina - 1); }} />
                                {(() => {
                                    const itens = [];
                                    const pageFinal = totalPaginas;
                                    const page_config = { limite: 15, min_paginas: 3 };
                                    let ultimaPaginaFoiOculta = false;
                                    for (let numeroPagina = 1; numeroPagina <= pageFinal; numeroPagina++) {
                                        const mostrar = (
                                            pageFinal <= page_config.limite ||
                                            (pagina - 2 <= numeroPagina && pagina + 2 >= numeroPagina) ||
                                            pagina === numeroPagina ||
                                            numeroPagina <= page_config.min_paginas ||
                                            numeroPagina > pageFinal - page_config.min_paginas
                                        );
                                        if (mostrar) {
                                            itens.push(
                                                <Pagination.Item key={numeroPagina} active={numeroPagina === pagina} onClick={() => setPagina(numeroPagina)}>
                                                    {numeroPagina}
                                                </Pagination.Item>
                                            );
                                            ultimaPaginaFoiOculta = false;
                                        } else {
                                            if (!ultimaPaginaFoiOculta) {
                                                let numeroPaginaOculta;
                                                if (pagina < numeroPagina) numeroPaginaOculta = pagina + page_config.min_paginas + 2;
                                                else numeroPaginaOculta = pagina - page_config.min_paginas - 2;
                                                if (numeroPaginaOculta > pageFinal || numeroPaginaOculta < 1) numeroPaginaOculta = pagina;
                                                itens.push(
                                                    <Pagination.Item key={"oculto-" + numeroPagina} active={false} onClick={() => setPagina(numeroPaginaOculta)}>
                                                        ...
                                                    </Pagination.Item>
                                                );
                                                ultimaPaginaFoiOculta = true;
                                            }
                                        }
                                    }
                                    return itens;
                                })()}
                                <Pagination.Next onClick={() => { if (pagina !== totalPaginas) setPagina(pagina + 1); }} />
                                <Pagination.Last onClick={() => setPagina(totalPaginas)} />
                            </Pagination>
                        </div>
                    </div>
                )}
            </Container>

            <ModalDommus
                open={openModal}
                close={() => {
                    setContatoSelecionado(null);
                    setOpenModal(false);
                }}
                titulo={contatoSelecionado ? "Editar Contato" : "Novo Contato"}
                size="md"
                classe="modal-contato"
            >
                <FormContato
                    contato={contatoSelecionado}
                    handleSalvar={handleSalvar}
                    closeModal={() => setOpenModal(false)}
                />
            </ModalDommus>
        </>
    );
}


